import { useEffect, useRef } from 'react';

/**
 * The house popover: paper card, hairline border, closes on an outside
 * mousedown or Escape. Same recipe as FeaturesPanel, pulled out here because
 * a block carries two of them.
 */
export default function MenuSurface({
  align = 'right',
  width,
  /** The wrapper holding both the trigger and this menu. A click on the
   *  trigger has to count as inside, or the menu would close on mousedown and
   *  immediately reopen on the trigger's click. */
  containerRef,
  onClose,
  children,
}: {
  align?: 'left' | 'right';
  width: number;
  containerRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const root = containerRef?.current ?? ref.current;
      if (root && !root.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, containerRef]);

  return (
    <div
      ref={ref}
      role="menu"
      style={{ width }}
      className={`
        absolute top-[calc(100%+6px)] ${align === 'right' ? 'right-0' : 'left-0'} py-1.5
        bg-card border border-line rounded-sm
        shadow-flare-lg/30 z-30 animate-rise-subtle
      `}
    >
      {children}
    </div>
  );
}

export function MenuLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-3 pb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-faint">
      {children}
    </p>
  );
}

export function MenuRow({
  onClick,
  danger = false,
  children,
}: {
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`
        w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[13px]
        hover:bg-elevated transition-colors duration-150
        ${danger ? 'text-error' : 'text-fg'}
      `}
    >
      {children}
    </button>
  );
}

export function MenuDivider() {
  return <div className="h-px bg-line my-1.5 mx-2" />;
}
